import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import {
  DEFAULT_CONTEXTUAL_ELEMENT,
  clearContextualSettings,
  loadContextualSettings,
  saveContextualSettings,
  type ContextualElementSettings,
  type ContextualSettingsMap,
} from './contextualSettings'

interface ContextValue {
  settings:
    ContextualSettingsMap

  get:
    (
      id: string,
    ) => ContextualElementSettings

  patch:
    (
      id: string,
      changes:
        Partial<ContextualElementSettings>,
    ) => void

  reset:
    (
      id: string,
    ) => void

  resetAll:
    () => void
}

const Context =
  createContext<
    ContextValue | null
  >(null)

export function ContextualSettingsProvider({
  children,
}: {
  children: ReactNode
}) {
  const [
    settings,
    setSettings,
  ] =
    useState<ContextualSettingsMap>(
      loadContextualSettings,
    )

  useEffect(() => {
    saveContextualSettings(
      settings,
    )
  }, [settings])

  const value =
    useMemo<ContextValue>(
      () => ({
        settings,

        get(id) {
          return {
            ...DEFAULT_CONTEXTUAL_ELEMENT,
            ...(settings[id] ?? {}),
          }
        },

        patch(
          id,
          changes,
        ) {
          setSettings(
            current => ({
              ...current,

              [id]: {
                ...DEFAULT_CONTEXTUAL_ELEMENT,
                ...(current[id] ?? {}),
                ...changes,
              },
            }),
          )
        },

        reset(id) {
          setSettings(
            current => {
              const next = {
                ...current,
              }

              delete next[id]

              return next
            },
          )
        },

        resetAll() {
          clearContextualSettings()
          setSettings({})
        },
      }),
      [settings],
    )

  return (
    <Context.Provider
      value={value}
    >
      {children}
    </Context.Provider>
  )
}

export function useContextualSettings() {
  const value =
    useContext(Context)

  if (!value) {
    throw new Error(
      'ContextualSettingsProvider missing',
    )
  }

  return value
}
